import React, { useState, useEffect } from 'react';
import { 
  Package, Plus, Edit2, Trash2, X, Save, Eye, EyeOff, 
  Building, MapPin, Star, Users, Calendar, DollarSign,
  Check, ChevronUp, ChevronDown, Image, Search, Link, ImagePlus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PackageManager = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [packageImages, setPackageImages] = useState([]);

  // Hotel search states
  const [makkahHotels, setMakkahHotels] = useState([]);
  const [madinahHotels, setMadinahHotels] = useState([]);
  const [makkahSearch, setMakkahSearch] = useState('');
  const [madinahSearch, setMadinahSearch] = useState('');
  const [showMakkahDropdown, setShowMakkahDropdown] = useState(false);
  const [showMadinahDropdown, setShowMadinahDropdown] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    duration_days: 10,
    duration_nights: 9,
    image_url: '',
    makkah_hotel: {
      name: '',
      stars: 4,
      sharing_type: 'QUAD SHARING',
      distance: '',
      nights: 5,
      website_url: '',
      images: []
    },
    madinah_hotel: {
      name: '',
      stars: 4,
      sharing_type: 'QUAD SHARING',
      distance: '',
      nights: 4,
      website_url: '',
      images: []
    },
    inclusions: ['Umrah Visa', 'Hotel Accommodation', 'Airport Transfers', 'Ziyarat Tours'],
    original_price: 1299,
    discounted_price: 999,
    spots_total: 150,
    spots_booked: 0,
    is_active: true,
    display_order: 0
  });

  const [newInclusion, setNewInclusion] = useState('');
  const [newMakkahImage, setNewMakkahImage] = useState('');
  const [newMadinahImage, setNewMadinahImage] = useState('');

  // Fetch packages
  const fetchPackages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/packages/?include_all=true`);
      const data = await response.json();
      setPackages(data.packages || []);
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
    fetchPackageImages();
  }, []);

  // Fetch package images
  const fetchPackageImages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/packages/images`);
      const data = await response.json();
      setPackageImages(data.images || []);
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  // Search Makkah hotels
  const searchMakkahHotels = async (query) => {
    setMakkahSearch(query);
    try {
      const response = await fetch(`${API_URL}/api/packages/hotels/makkah?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      setMakkahHotels(data.hotels || []);
      setShowMakkahDropdown(true);
    } catch (error) {
      console.error('Error searching Makkah hotels:', error);
    }
  };

  // Search Madinah hotels
  const searchMadinahHotels = async (query) => {
    setMadinahSearch(query);
    try {
      const response = await fetch(`${API_URL}/api/packages/hotels/madinah?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      setMadinahHotels(data.hotels || []);
      setShowMadinahDropdown(true);
    } catch (error) {
      console.error('Error searching Madinah hotels:', error);
    }
  };

  // Select Makkah hotel
  const selectMakkahHotel = (hotel) => {
    setFormData({
      ...formData,
      makkah_hotel: {
        ...formData.makkah_hotel,
        name: hotel.name,
        stars: hotel.stars,
        distance: hotel.distance
      }
    });
    setMakkahSearch(hotel.name);
    setShowMakkahDropdown(false);
  };

  // Select Madinah hotel
  const selectMadinahHotel = (hotel) => {
    setFormData({
      ...formData,
      madinah_hotel: {
        ...formData.madinah_hotel,
        name: hotel.name,
        stars: hotel.stars,
        distance: hotel.distance
      }
    });
    setMadinahSearch(hotel.name);
    setShowMadinahDropdown(false);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      duration_days: 10,
      duration_nights: 9,
      image_url: '',
      makkah_hotel: {
        name: '',
        stars: 4,
        sharing_type: 'QUAD SHARING',
        distance: '',
        nights: 5,
        website_url: '',
        images: []
      },
      madinah_hotel: {
        name: '',
        stars: 4,
        sharing_type: 'QUAD SHARING',
        distance: '',
        nights: 4,
        website_url: '',
        images: []
      },
      inclusions: ['Umrah Visa', 'Hotel Accommodation', 'Airport Transfers', 'Ziyarat Tours'],
      original_price: 1299,
      discounted_price: 999,
      spots_total: 150,
      spots_booked: 0,
      is_active: true,
      display_order: packages.length + 1
    });
    setEditingPackage(null);
    setNewMakkahImage('');
    setNewMadinahImage('');
  };

  // Open editor for new package
  const handleNew = () => {
    resetForm();
    setMakkahSearch('');
    setMadinahSearch('');
    setShowEditor(true);
  };

  // Open editor for editing
  const handleEdit = (pkg) => {
    setFormData({
      name: pkg.name,
      duration_days: pkg.duration_days,
      duration_nights: pkg.duration_nights,
      image_url: pkg.image_url || '',
      makkah_hotel: {
        ...pkg.makkah_hotel,
        website_url: pkg.makkah_hotel?.website_url || '',
        images: pkg.makkah_hotel?.images || []
      },
      madinah_hotel: {
        ...pkg.madinah_hotel,
        website_url: pkg.madinah_hotel?.website_url || '',
        images: pkg.madinah_hotel?.images || []
      },
      inclusions: pkg.inclusions || [],
      original_price: pkg.original_price,
      discounted_price: pkg.discounted_price,
      spots_total: pkg.spots_total,
      spots_booked: pkg.spots_booked,
      is_active: pkg.is_active,
      display_order: pkg.display_order
    });
    // Set search fields to show current hotel names
    setMakkahSearch(pkg.makkah_hotel?.name || '');
    setMadinahSearch(pkg.madinah_hotel?.name || '');
    setNewMakkahImage('');
    setNewMadinahImage('');
    setEditingPackage(pkg);
    setShowEditor(true);
  };

  // Save package
  const handleSave = async () => {
    setSaving(true);
    try {
      const url = editingPackage 
        ? `${API_URL}/api/packages/${editingPackage.package_id}`
        : `${API_URL}/api/packages/`;
      
      const method = editingPackage ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchPackages();
        setShowEditor(false);
        resetForm();
      }
    } catch (error) {
      console.error('Error saving package:', error);
    } finally {
      setSaving(false);
    }
  };

  // Delete package
  const handleDelete = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    
    try {
      await fetch(`${API_URL}/api/packages/${packageId}`, { method: 'DELETE' });
      fetchPackages();
    } catch (error) {
      console.error('Error deleting package:', error);
    }
  };

  // Toggle active status
  const toggleActive = async (pkg) => {
    try {
      await fetch(`${API_URL}/api/packages/${pkg.package_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !pkg.is_active })
      });
      fetchPackages();
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  // Add inclusion
  const addInclusion = () => {
    if (newInclusion.trim()) {
      setFormData({
        ...formData,
        inclusions: [...formData.inclusions, newInclusion.trim()]
      });
      setNewInclusion('');
    }
  };

  // Remove inclusion
  const removeInclusion = (index) => {
    setFormData({
      ...formData,
      inclusions: formData.inclusions.filter((_, i) => i !== index)
    });
  };

  // Render stars
  const renderStars = (count) => {
    return Array(count).fill(0).map((_, i) => (
      <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
    ));
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Package className="w-8 h-8 text-[#d4af37]" />
          <div>
            <h2 className="text-xl font-bold text-gray-800">Package Management</h2>
            <p className="text-sm text-gray-500">Create and edit Umrah packages</p>
          </div>
        </div>
        <button
          onClick={handleNew}
          className="px-4 py-2 bg-[#d4af37] text-[#0a1628] rounded-lg font-semibold flex items-center gap-2 hover:bg-[#c9a432] transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Package
        </button>
      </div>

      {/* Packages Grid */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading packages...</div>
      ) : packages.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No packages found. Create your first package!</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <div 
              key={pkg.package_id} 
              className={`bg-white rounded-xl shadow-lg overflow-hidden ${!pkg.is_active ? 'opacity-60' : ''}`}
            >
              {/* Image */}
              <div className="relative h-48 bg-gray-200">
                {pkg.image_url ? (
                  <img src={pkg.image_url} alt={pkg.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-12 h-12 text-gray-400" />
                  </div>
                )}
                <div className="absolute top-3 left-3 bg-[#d4af37] text-[#0a1628] px-3 py-1 rounded-full text-sm font-bold">
                  {pkg.duration_days} Days / {pkg.duration_nights} Nights
                </div>
                {!pkg.is_active && (
                  <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                    Hidden
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="text-xl font-bold text-gray-800 mb-3">{pkg.name}</h3>

                {/* Hotels */}
                <div className="space-y-3 mb-4">
                  {/* Makkah */}
                  <div className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg">
                    <Building className="w-5 h-5 text-[#d4af37] mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm">MAKKAH</span>
                        <span className="text-xs bg-[#d4af37]/20 text-[#d4af37] px-2 py-0.5 rounded">
                          {pkg.makkah_hotel.nights} Nights
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">{pkg.makkah_hotel.name}</div>
                      <div className="flex items-center gap-1 mt-1">
                        {renderStars(pkg.makkah_hotel.stars)}
                        <span className="text-xs text-gray-500 ml-1">({pkg.makkah_hotel.sharing_type})</span>
                      </div>
                    </div>
                  </div>

                  {/* Madinah */}
                  <div className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg">
                    <Building className="w-5 h-5 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm">MADINAH</span>
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                          {pkg.madinah_hotel.nights} Nights
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">{pkg.madinah_hotel.name}</div>
                      <div className="flex items-center gap-1 mt-1">
                        {renderStars(pkg.madinah_hotel.stars)}
                        <span className="text-xs text-gray-500 ml-1">({pkg.madinah_hotel.sharing_type})</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Inclusions */}
                <div className="flex flex-wrap gap-1 mb-4">
                  {pkg.inclusions.slice(0, 4).map((inc, i) => (
                    <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full flex items-center gap-1">
                      <Check className="w-3 h-3 text-green-500" />
                      {inc}
                    </span>
                  ))}
                  {pkg.inclusions.length > 4 && (
                    <span className="text-xs text-gray-500">+{pkg.inclusions.length - 4} more</span>
                  )}
                </div>

                {/* Price */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-gray-400 line-through text-sm">£{pkg.original_price}</span>
                    <span className="text-2xl font-bold text-[#d4af37] ml-2">£{pkg.discounted_price}</span>
                    <span className="text-gray-500 text-sm">/person</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {pkg.spots_booked}/{pkg.spots_total} spots
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(pkg)}
                    className="flex-1 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 flex items-center justify-center gap-1"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => toggleActive(pkg)}
                    className={`px-3 py-2 rounded-lg font-medium flex items-center justify-center ${
                      pkg.is_active 
                        ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                    title={pkg.is_active ? 'Hide package' : 'Show package'}
                  >
                    {pkg.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => handleDelete(pkg.package_id)}
                    className="px-3 py-2 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200"
                    title="Delete package"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-bold">
                {editingPackage ? 'Edit Package' : 'Create New Package'}
              </h3>
              <button onClick={() => setShowEditor(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Package Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37]"
                    placeholder="e.g. Economy Umrah"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (Days)</label>
                  <input
                    type="number"
                    value={formData.duration_days}
                    onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (Nights)</label>
                  <input
                    type="number"
                    value={formData.duration_nights}
                    onChange={(e) => setFormData({ ...formData, duration_nights: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37]"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Package Image</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37]"
                      placeholder="https://... or click Browse"
                    />
                    <button
                      type="button"
                      onClick={() => setShowImagePicker(true)}
                      className="px-4 py-2 bg-[#d4af37] text-[#0a1628] rounded-lg font-medium hover:bg-[#c9a432] flex items-center gap-2"
                    >
                      <Image className="w-4 h-4" />
                      Browse
                    </button>
                  </div>
                  {formData.image_url && (
                    <div className="mt-2 w-32 h-20 rounded-lg overflow-hidden bg-gray-100">
                      <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
              </div>

              {/* Makkah Hotel */}
              <div className="bg-amber-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Building className="w-5 h-5 text-[#d4af37]" />
                  Makkah Hotel
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Name (Search)</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={makkahSearch}
                        onChange={(e) => {
                          setMakkahSearch(e.target.value);
                          searchMakkahHotels(e.target.value);
                        }}
                        onFocus={() => searchMakkahHotels(makkahSearch || '')}
                        className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37]"
                        placeholder="Search Makkah hotels..."
                      />
                      {makkahSearch && (
                        <button
                          type="button"
                          onClick={() => {
                            setMakkahSearch('');
                            setFormData({
                              ...formData,
                              makkah_hotel: { ...formData.makkah_hotel, name: '', distance: '' }
                            });
                            searchMakkahHotels('');
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {showMakkahDropdown && makkahHotels.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {makkahHotels.map((hotel) => (
                          <div
                            key={hotel.id}
                            onClick={() => selectMakkahHotel(hotel)}
                            className="px-4 py-3 hover:bg-amber-50 cursor-pointer border-b border-gray-100 last:border-0"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-sm text-gray-800">{hotel.name}</span>
                              <span className="text-yellow-500 text-sm font-bold whitespace-nowrap">
                                {'★'.repeat(hotel.stars)}{'☆'.repeat(5 - hotel.stars)}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                              <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-medium">{hotel.stars}-Star</span>
                              <span>• {hotel.distance}</span>
                              <span className="text-gray-400">• {hotel.category}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nights</label>
                    <input
                      type="number"
                      value={formData.makkah_hotel.nights}
                      onChange={(e) => setFormData({
                        ...formData,
                        makkah_hotel: { ...formData.makkah_hotel, nights: parseInt(e.target.value) || 0 }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stars</label>
                    <select
                      value={formData.makkah_hotel.stars}
                      onChange={(e) => setFormData({
                        ...formData,
                        makkah_hotel: { ...formData.makkah_hotel, stars: parseInt(e.target.value) }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value={3}>3 Stars</option>
                      <option value={4}>4 Stars</option>
                      <option value={5}>5 Stars</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sharing Type</label>
                    <select
                      value={formData.makkah_hotel.sharing_type}
                      onChange={(e) => setFormData({
                        ...formData,
                        makkah_hotel: { ...formData.makkah_hotel, sharing_type: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="DOUBLE SHARING">Double Sharing</option>
                      <option value="TRIPLE SHARING">Triple Sharing</option>
                      <option value="QUAD SHARING">Quad Sharing</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Distance</label>
                    <input
                      type="text"
                      value={formData.makkah_hotel.distance}
                      onChange={(e) => setFormData({
                        ...formData,
                        makkah_hotel: { ...formData.makkah_hotel, distance: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="e.g. 200 M To Masjidil Haram"
                    />
                  </div>
                  {/* Hotel Website Link */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <Link className="w-4 h-4" />
                      Hotel Website URL
                    </label>
                    <input
                      type="url"
                      value={formData.makkah_hotel.website_url || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        makkah_hotel: { ...formData.makkah_hotel, website_url: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="https://www.hotel-website.com"
                    />
                  </div>
                  {/* Hotel Images */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <ImagePlus className="w-4 h-4" />
                      Hotel Pictures (for customers to view)
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(formData.makkah_hotel.images || []).map((img, i) => (
                        <div key={i} className="relative group">
                          <img src={img} alt={`Hotel ${i + 1}`} className="w-20 h-16 object-cover rounded border" />
                          <button
                            type="button"
                            onClick={() => {
                              const newImages = formData.makkah_hotel.images.filter((_, idx) => idx !== i);
                              setFormData({
                                ...formData,
                                makkah_hotel: { ...formData.makkah_hotel, images: newImages }
                              });
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={newMakkahImage}
                        onChange={(e) => setNewMakkahImage(e.target.value)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="Paste image URL here..."
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && newMakkahImage.trim()) {
                            e.preventDefault();
                            setFormData({
                              ...formData,
                              makkah_hotel: {
                                ...formData.makkah_hotel,
                                images: [...(formData.makkah_hotel.images || []), newMakkahImage.trim()]
                              }
                            });
                            setNewMakkahImage('');
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (newMakkahImage.trim()) {
                            setFormData({
                              ...formData,
                              makkah_hotel: {
                                ...formData.makkah_hotel,
                                images: [...(formData.makkah_hotel.images || []), newMakkahImage.trim()]
                              }
                            });
                            setNewMakkahImage('');
                          }
                        }}
                        className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 text-sm font-medium"
                      >
                        Add
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Add multiple hotel room/interior pictures for customers</p>
                  </div>
                </div>
              </div>

              {/* Madinah Hotel */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Building className="w-5 h-5 text-green-600" />
                  Madinah Hotel
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Name (Search)</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={madinahSearch}
                        onChange={(e) => {
                          setMadinahSearch(e.target.value);
                          searchMadinahHotels(e.target.value);
                        }}
                        onFocus={() => searchMadinahHotels(madinahSearch || '')}
                        className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        placeholder="Search Madinah hotels..."
                      />
                      {madinahSearch && (
                        <button
                          type="button"
                          onClick={() => {
                            setMadinahSearch('');
                            setFormData({
                              ...formData,
                              madinah_hotel: { ...formData.madinah_hotel, name: '', distance: '' }
                            });
                            searchMadinahHotels('');
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {showMadinahDropdown && madinahHotels.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {madinahHotels.map((hotel) => (
                          <div
                            key={hotel.id}
                            onClick={() => selectMadinahHotel(hotel)}
                            className="px-4 py-3 hover:bg-green-50 cursor-pointer border-b border-gray-100 last:border-0"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-sm text-gray-800">{hotel.name}</span>
                              <span className="text-yellow-500 text-sm font-bold whitespace-nowrap">
                                {'★'.repeat(hotel.stars)}{'☆'.repeat(5 - hotel.stars)}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                              <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">{hotel.stars}-Star</span>
                              <span>• {hotel.distance}</span>
                              <span className="text-gray-400">• {hotel.category}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nights</label>
                    <input
                      type="number"
                      value={formData.madinah_hotel.nights}
                      onChange={(e) => setFormData({
                        ...formData,
                        madinah_hotel: { ...formData.madinah_hotel, nights: parseInt(e.target.value) || 0 }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stars</label>
                    <select
                      value={formData.madinah_hotel.stars}
                      onChange={(e) => setFormData({
                        ...formData,
                        madinah_hotel: { ...formData.madinah_hotel, stars: parseInt(e.target.value) }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value={3}>3 Stars</option>
                      <option value={4}>4 Stars</option>
                      <option value={5}>5 Stars</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sharing Type</label>
                    <select
                      value={formData.madinah_hotel.sharing_type}
                      onChange={(e) => setFormData({
                        ...formData,
                        madinah_hotel: { ...formData.madinah_hotel, sharing_type: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="DOUBLE SHARING">Double Sharing</option>
                      <option value="TRIPLE SHARING">Triple Sharing</option>
                      <option value="QUAD SHARING">Quad Sharing</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Distance</label>
                    <input
                      type="text"
                      value={formData.madinah_hotel.distance}
                      onChange={(e) => setFormData({
                        ...formData,
                        madinah_hotel: { ...formData.madinah_hotel, distance: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="e.g. 100 M To Masjid Nabawi"
                    />
                  </div>
                  {/* Hotel Website Link */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <Link className="w-4 h-4" />
                      Hotel Website URL
                    </label>
                    <input
                      type="url"
                      value={formData.madinah_hotel.website_url || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        madinah_hotel: { ...formData.madinah_hotel, website_url: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="https://www.hotel-website.com"
                    />
                  </div>
                  {/* Hotel Images */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <ImagePlus className="w-4 h-4" />
                      Hotel Pictures (for customers to view)
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(formData.madinah_hotel.images || []).map((img, i) => (
                        <div key={i} className="relative group">
                          <img src={img} alt={`Hotel ${i + 1}`} className="w-20 h-16 object-cover rounded border" />
                          <button
                            type="button"
                            onClick={() => {
                              const newImages = formData.madinah_hotel.images.filter((_, idx) => idx !== i);
                              setFormData({
                                ...formData,
                                madinah_hotel: { ...formData.madinah_hotel, images: newImages }
                              });
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={newMadinahImage}
                        onChange={(e) => setNewMadinahImage(e.target.value)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="Paste image URL here..."
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && newMadinahImage.trim()) {
                            e.preventDefault();
                            setFormData({
                              ...formData,
                              madinah_hotel: {
                                ...formData.madinah_hotel,
                                images: [...(formData.madinah_hotel.images || []), newMadinahImage.trim()]
                              }
                            });
                            setNewMadinahImage('');
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (newMadinahImage.trim()) {
                            setFormData({
                              ...formData,
                              madinah_hotel: {
                                ...formData.madinah_hotel,
                                images: [...(formData.madinah_hotel.images || []), newMadinahImage.trim()]
                              }
                            });
                            setNewMadinahImage('');
                          }
                        }}
                        className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm font-medium"
                      >
                        Add
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Add multiple hotel room/interior pictures for customers</p>
                  </div>
                </div>
              </div>

              {/* Inclusions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Inclusions</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.inclusions.map((inc, i) => (
                    <span key={i} className="bg-gray-100 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                      {inc}
                      <button onClick={() => removeInclusion(i)} className="text-red-500 hover:text-red-700">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newInclusion}
                    onChange={(e) => setNewInclusion(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Add inclusion (e.g. Breakfast)"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInclusion())}
                  />
                  <button
                    onClick={addInclusion}
                    className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Original Price (£)</label>
                  <input
                    type="number"
                    value={formData.original_price}
                    onChange={(e) => setFormData({ ...formData, original_price: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discounted Price (£)</label>
                  <input
                    type="number"
                    value={formData.discounted_price}
                    onChange={(e) => setFormData({ ...formData, discounted_price: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Spots</label>
                  <input
                    type="number"
                    value={formData.spots_total}
                    onChange={(e) => setFormData({ ...formData, spots_total: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Spots Booked</label>
                  <input
                    type="number"
                    value={formData.spots_booked}
                    onChange={(e) => setFormData({ ...formData, spots_booked: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              {/* Display Order & Active */}
              <div className="flex items-center gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                    className="w-24 px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                    Active (visible on website)
                  </label>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t flex justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowEditor(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.name}
                className="px-6 py-2 bg-[#d4af37] text-[#0a1628] rounded-lg font-semibold hover:bg-[#c9a432] disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Package'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Picker Modal */}
      {showImagePicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Image className="w-5 h-5 text-[#d4af37]" />
                Select Package Image
              </h3>
              <button onClick={() => setShowImagePicker(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {packageImages.map((img) => (
                  <div
                    key={img.id}
                    onClick={() => {
                      setFormData({ ...formData, image_url: img.url });
                      setShowImagePicker(false);
                    }}
                    className={`cursor-pointer rounded-lg overflow-hidden border-2 transition-all hover:border-[#d4af37] hover:shadow-lg ${
                      formData.image_url === img.url ? 'border-[#d4af37] ring-2 ring-[#d4af37]' : 'border-gray-200'
                    }`}
                  >
                    <div className="aspect-video bg-gray-100">
                      <img src={img.url} alt={img.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-2 bg-white">
                      <div className="text-sm font-medium text-gray-800 truncate">{img.name}</div>
                      <div className="text-xs text-gray-500 capitalize">{img.category}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageManager;
